import {
  Card,
  Container,
  createTheme,
  Paper,
  rem,
  Select,
  Button,
  TextInput,
  PasswordInput,
  Tabs,
  Title,
  Modal,
  MantineColorsTuple,
} from '@mantine/core';
import type { MantineThemeOverride } from '@mantine/core';

const CONTAINER_SIZES: Record<string, string> = {
  xxs: rem('200px'),
  xs: rem('300px'),
  sm: rem('400px'),
  md: rem('500px'),
  lg: rem('800px'),
  xl: rem('1240px'),
  xxl: rem('1400px'),
};

// Brand colors (consistent across themes)
const brandColors: MantineColorsTuple = [
  '#f3e8ff', // 50
  '#e9d5ff', // 100
  '#d8b4fe', // 200
  '#c084fc', // 300
  '#a855f7', // 400
  '#8e36e1', // 500 - Primary
  '#7c3aed', // 600
  '#6d28d9', // 700
  '#5b21b6', // 800
  '#4c1d95', // 900
];

const secondaryColors: MantineColorsTuple = [
  '#eef2ff', // 50
  '#e0e7ff', // 100
  '#c7d2fe', // 200
  '#a5b4fc', // 300
  '#818cf8', // 400
  '#4f46e5', // 500 - Secondary
  '#4338ca', // 600
  '#3730a3', // 700
  '#312e81', // 800
  '#1e1b4b', // 900
];

const tertiaryColors: MantineColorsTuple = [
  '#ecfdf5', // 50
  '#d1fae5', // 100
  '#a7f3d0', // 200
  '#6ee7b7', // 300
  '#34d399', // 400
  '#10b981', // 500 - Tertiary
  '#059669', // 600
  '#047857', // 700
  '#065f46', // 800
  '#064e3b', // 900
];

// Light theme gray colors
const lightGrayColors: MantineColorsTuple = [
  '#f8fafc', // 50 - Lightest background
  '#f1f5f9', // 100 - Card backgrounds
  '#e2e8f0', // 200 - Unfilled bars, borders
  '#cbd5e1', // 300 - Dividers, inactive states
  '#94a3b8', // 400 - Placeholder text
  '#64748b', // 500 - Muted text
  '#475569', // 600 - Secondary text
  '#334155', // 700 - Primary text (lighter)
  '#1e293b', // 800 - Primary text
  '#0f172a', // 900 - Darkest text
];

// Dark theme gray colors (inverted and adjusted)
const darkGrayColors: MantineColorsTuple = [
  '#0f172a', // 0 - Darkest background
  '#1e293b', // 1 - Card backgrounds
  '#334155', // 2 - Borders, dividers
  '#475569', // 3 - Inactive states
  '#64748b', // 4 - Muted text
  '#94a3b8', // 5 - Secondary text
  '#cbd5e1', // 6 - Primary text
  '#e2e8f0', // 7 - Light text
  '#f1f5f9', // 8 - Lighter text
  '#f8fafc', // 9 - Lightest text
];

const baseTheme: MantineThemeOverride = createTheme({
  fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, sans-serif',
  fontSizes: {
    xs: rem('12px'),
    sm: rem('14px'),
    md: rem('16px'),
    lg: rem('18px'),
    xl: rem('20px'),
    '2xl': rem('24px'),
    '3xl': rem('30px'),
    '4xl': rem('36px'),
    '5xl': rem('48px'),
  },
  spacing: {
    '3xs': rem('4px'),
    '2xs': rem('8px'),
    xs: rem('10px'),
    sm: rem('12px'),
    md: rem('16px'),
    lg: rem('20px'),
    xl: rem('24px'),
    '2xl': rem('28px'),
    '3xl': rem('32px'),
  },
  primaryColor: 'brand',
  colors: {
    brand: brandColors,
    secondary: secondaryColors,
    tertiary: tertiaryColors,
  },
  defaultRadius: 'md',
  components: {
    Container: Container.extend({
      vars: (_, { size, fluid }) => ({
        root: {
          '--container-size': fluid
            ? '100%'
            : size !== undefined && size in CONTAINER_SIZES
              ? CONTAINER_SIZES[size]
              : rem(size),
        },
      }),
    }),
    Button: Button.extend({
      defaultProps: {
        size: 'md',
        radius: 'xl',
        fw: 600,
      },
      styles: {
        root: {
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
          height: rem('48px'),
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    }),
    TextInput: TextInput.extend({
      defaultProps: {
        size: 'md',
        radius: 'md',
      },
      styles: {
        input: {
          height: rem('48px'),
        },
        label: {
          fontSize: rem('14px'),
          fontWeight: 500,
          marginBottom: rem('6px'),
        },
      },
    }),
    PasswordInput: PasswordInput.extend({
      defaultProps: {
        size: 'md',
        radius: 'md',
      },
      styles: {
        input: {
          height: rem('48px'),
        },
        label: {
          fontSize: rem('14px'),
          fontWeight: 500,
          marginBottom: rem('6px'),
        },
      },
    }),
    Tabs: Tabs.extend({
      defaultProps: {
        radius: 'lg',
        variant: 'outline',
      },
      styles: theme => ({
        tab: {
          fontWeight: 600,
          height: rem('48px'),
          '&:hover': {
            color: 'var(--mantine-color-brand-5)',
          },
        },
        panel: {
          paddingTop: theme.spacing.xl,
        },
      }),
    }),
    Select: Select.extend({
      defaultProps: {
        checkIconPosition: 'right',
        size: 'md',
        radius: 'md',
      },
      styles: {
        input: {
          height: rem('48px'),
        },
        label: {
          fontSize: rem('14px'),
          fontWeight: 500,
          marginBottom: rem('6px'),
        },
      },
    }),
    Modal: Modal.extend({
      defaultProps: {
        size: 'lg',
        centered: true,
      },
      styles: {
        header: {
          paddingBottom: rem('16px'),
          marginBottom: rem('16px'),
          borderBottom: '1px solid var(--mantine-color-gray-2)',
        },
        body: {
          paddingTop: rem('0px'),
        },
        title: {
          fontSize: rem('20px'),
          fontWeight: 600,
        },
      },
    }),
  },
  other: {
    style: 'lemonade',
    headerHeight: rem('80px'),
    headerHeightWithImpersonation: rem('118px'),
    footerHeight: rem('60px'),
    maxContentWidth: rem('1140px'),
    colors: {
      success: 'var(--mantine-color-tertiary-5)',
      warning: '#f59e0b',
      error: '#ef4444',
      info: 'var(--mantine-color-secondary-5)',
    },
  },
});

// Light theme
export const lightTheme: MantineThemeOverride = createTheme({
  ...baseTheme,
  colors: {
    ...baseTheme.colors,
    gray: lightGrayColors,
  },
  components: {
    ...baseTheme.components,
    Paper: Paper.extend({
      defaultProps: {
        p: 'xl',
        shadow: 'sm',
        radius: 'lg',
        withBorder: false,
      },
      styles: {
        root: {
          backgroundColor: 'var(--mantine-color-gray-0)',
          borderColor: 'var(--mantine-color-gray-2)',
        },
      },
    }),
    Card: Card.extend({
      defaultProps: {
        p: 'xl',
        shadow: 'sm',
        radius: 'lg',
        withBorder: false,
      },
      styles: {
        root: {
          backgroundColor: 'var(--mantine-color-gray-0)',
          borderColor: 'var(--mantine-color-gray-2)',
        },
      },
    }),
    TextInput: TextInput.extend({
      ...baseTheme.components?.TextInput,
      styles: {
        input: {
          height: rem('48px'),
          borderColor: 'var(--mantine-color-gray-3)',
          '&:focus': {
            borderColor: 'var(--mantine-color-brand-5)',
          },
        },
        label: {
          fontSize: rem('14px'),
          fontWeight: 500,
          marginBottom: rem('6px'),
          color: 'var(--mantine-color-gray-7)',
        },
      },
    }),
    PasswordInput: PasswordInput.extend({
      ...baseTheme.components?.PasswordInput,
      styles: {
        input: {
          height: rem('48px'),
          borderColor: 'var(--mantine-color-gray-3)',
          '&:focus': {
            borderColor: 'var(--mantine-color-brand-5)',
          },
        },
        label: {
          fontSize: rem('14px'),
          fontWeight: 500,
          marginBottom: rem('6px'),
          color: 'var(--mantine-color-gray-7)',
        },
      },
    }),
    Tabs: Tabs.extend({
      ...baseTheme.components?.Tabs,
      styles: theme => ({
        tab: {
          fontWeight: 600,
          height: rem('48px'),
          color: 'var(--mantine-color-gray-6)',
          '&[dataActive]': {
            color: 'var(--mantine-color-brand-6)',
            borderColor: 'var(--mantine-color-brand-6)',
          },
          '&:hover': {
            color: 'var(--mantine-color-brand-5)',
          },
        },
        panel: {
          paddingTop: theme.spacing.xl,
        },
      }),
    }),
    Title: Title.extend({
      defaultProps: {
        c: 'gray.8',
      },
    }),
    Select: Select.extend({
      ...baseTheme.components?.Select,
      styles: {
        input: {
          height: rem('48px'),
          borderColor: 'var(--mantine-color-gray-3)',
          '&:focus': {
            borderColor: 'var(--mantine-color-brand-5)',
          },
        },
        label: {
          fontSize: rem('14px'),
          fontWeight: 500,
          marginBottom: rem('6px'),
          color: 'var(--mantine-color-gray-7)',
        },
      },
    }),
    Modal: Modal.extend({
      defaultProps: {
        size: 'lg',
        centered: true,
      },
      styles: {
        header: {
          paddingBottom: rem('16px'),
          marginBottom: rem('16px'),
          borderBottom: '1px solid var(--mantine-color-gray-2)',
        },
        body: {
          paddingTop: rem('0px'),
        },
        title: {
          fontSize: rem('20px'),
          fontWeight: 600,
          color: 'var(--mantine-color-gray-8)',
        },
      },
    }),
  },
});

// Dark theme
export const darkTheme: MantineThemeOverride = createTheme({
  ...baseTheme,
  colors: {
    ...baseTheme.colors,
    gray: darkGrayColors,
  },
  components: {
    ...baseTheme.components,
    Paper: Paper.extend({
      defaultProps: {
        p: 'xl',
        shadow: 'sm',
        radius: 'lg',
        withBorder: false,
      },
      styles: {
        root: {
          backgroundColor: 'var(--mantine-color-gray-1)',
          borderColor: 'var(--mantine-color-gray-3)',
        },
      },
    }),
    Card: Card.extend({
      defaultProps: {
        p: 'xl',
        shadow: 'sm',
        radius: 'lg',
        withBorder: false,
      },
      styles: {
        root: {
          backgroundColor: 'var(--mantine-color-gray-1)',
          borderColor: 'var(--mantine-color-gray-3)',
        },
      },
    }),
    TextInput: TextInput.extend({
      ...baseTheme.components?.TextInput,
      styles: {
        input: {
          height: rem('48px'),
          borderColor: 'var(--mantine-color-gray-3)',
          backgroundColor: 'var(--mantine-color-gray-1)',
          color: 'var(--mantine-color-gray-7)',
          '&:focus': {
            borderColor: 'var(--mantine-color-brand-5)',
          },
        },
        label: {
          fontSize: rem('14px'),
          fontWeight: 500,
          marginBottom: rem('6px'),
          color: 'var(--mantine-color-gray-6)',
        },
      },
    }),
    PasswordInput: PasswordInput.extend({
      ...baseTheme.components?.PasswordInput,
      styles: {
        input: {
          height: rem('48px'),
          borderColor: 'var(--mantine-color-gray-3)',
          backgroundColor: 'var(--mantine-color-gray-1)',
          color: 'var(--mantine-color-gray-7)',
          '&:focus': {
            borderColor: 'var(--mantine-color-brand-5)',
          },
        },
        label: {
          fontSize: rem('14px'),
          fontWeight: 500,
          marginBottom: rem('6px'),
          color: 'var(--mantine-color-gray-6)',
        },
      },
    }),
    Tabs: Tabs.extend({
      ...baseTheme.components?.Tabs,
      styles: theme => ({
        tab: {
          fontWeight: 600,
          height: rem('48px'),
          color: 'var(--mantine-color-gray-5)',
          '&[dataActive]': {
            color: 'var(--mantine-color-brand-4)',
            borderColor: 'var(--mantine-color-brand-4)',
          },
          '&:hover': {
            color: 'var(--mantine-color-brand-3)',
          },
        },
        panel: {
          paddingTop: theme.spacing.xl,
        },
      }),
    }),
    Title: Title.extend({
      defaultProps: {
        c: 'gray.7',
      },
    }),
    Select: Select.extend({
      ...baseTheme.components?.Select,
      styles: {
        input: {
          height: rem('48px'),
          borderColor: 'var(--mantine-color-gray-3)',
          backgroundColor: 'var(--mantine-color-gray-1)',
          color: 'var(--mantine-color-gray-7)',
          '&:focus': {
            borderColor: 'var(--mantine-color-brand-5)',
          },
        },
        label: {
          fontSize: rem('14px'),
          fontWeight: 500,
          marginBottom: rem('6px'),
          color: 'var(--mantine-color-gray-6)',
        },
      },
    }),
    Modal: Modal.extend({
      defaultProps: {
        size: 'lg',
        centered: true,
      },
      styles: {
        header: {
          paddingBottom: rem('16px'),
          marginBottom: rem('16px'),
          borderBottom: '1px solid var(--mantine-color-gray-3)',
        },
        body: {
          paddingTop: rem('0px'),
        },
        title: {
          fontSize: rem('20px'),
          fontWeight: 600,
          color: 'var(--mantine-color-gray-7)',
        },
      },
    }),
  },
});

// Unified theme that works with Mantine's color scheme system
export const mantineTheme: MantineThemeOverride = createTheme({
  ...baseTheme,
  colors: {
    ...baseTheme.colors,
    gray: lightGrayColors,
    // Add dark colors as a separate color tuple for CSS reference
    dark: darkGrayColors,
  },
  components: {
    ...baseTheme.components,
    Paper: Paper.extend({
      defaultProps: {
        p: 'xl',
        shadow: 'sm',
        radius: 'lg',
        withBorder: false,
      },
    }),
    Card: Card.extend({
      defaultProps: {
        p: 'xl',
        shadow: 'sm',
        radius: 'lg',
        withBorder: false,
      },
    }),
    TextInput: TextInput.extend({
      defaultProps: {
        size: 'md',
        radius: 'md',
      },
      styles: {
        input: {
          height: rem('48px'),
        },
        label: {
          fontSize: rem('14px'),
          fontWeight: 500,
          marginBottom: rem('6px'),
        },
      },
    }),
    PasswordInput: PasswordInput.extend({
      defaultProps: {
        size: 'md',
        radius: 'md',
      },
      styles: {
        input: {
          height: rem('48px'),
        },
        label: {
          fontSize: rem('14px'),
          fontWeight: 500,
          marginBottom: rem('6px'),
        },
      },
    }),
    Tabs: Tabs.extend({
      defaultProps: {
        radius: 'lg',
        variant: 'outline',
      },
      styles: theme => ({
        tab: {
          fontWeight: 600,
          height: rem('48px'),
        },
        panel: {
          paddingTop: theme.spacing.xl,
        },
      }),
    }),
    Select: Select.extend({
      defaultProps: {
        checkIconPosition: 'right',
        size: 'md',
        radius: 'md',
      },
      styles: {
        input: {
          height: rem('48px'),
        },
        label: {
          fontSize: rem('14px'),
          fontWeight: 500,
          marginBottom: rem('6px'),
        },
      },
    }),
    Modal: Modal.extend({
      defaultProps: {
        size: 'lg',
        centered: true,
      },
      styles: {
        header: {
          paddingBottom: rem('16px'),
          marginBottom: rem('16px'),
          borderBottom: '1px solid var(--mantine-color-gray-2)',
        },
        body: {
          paddingTop: rem('0px'),
        },
        title: {
          fontSize: rem('20px'),
          fontWeight: 600,
        },
      },
    }),
  },
});
